/*
 * DSD Notification Registry Foundation - p11dz151
 * Registry-only notification infrastructure contract. Defines notification source,
 * target, priority, scope, lifecycle, retention, delivery channel, aggregation,
 * digest, and future Firebase path ownership. No Firebase reads/writes/realtime.
 * No notification dispatch. No UI behavior changes.
 */
(function(){
  'use strict';
  if (window.DSDNotificationRegistry && window.DSDNotificationRegistry.version) return;

  var VERSION = 'p11dz151-notification-registry-foundation-v1';
  var OWNER = 'NotificationRegistry';
  var FIREBASE_READS_ENABLED = false;
  var FIREBASE_WRITES_ENABLED = false;
  var REALTIME_ENABLED = false;
  var DISPATCH_ENABLED = false;
  var UI_DELIVERY_ENABLED = false;
  var DIGEST_ENABLED = false;

  function registry(){ return window.DSDRegistry || window.DSDRuntimeRegistry || null; }
  function claim(surface, meta){
    try {
      var r = registry();
      var payload = Object.assign({ version: VERSION }, meta || {});
      if (r && typeof r.claim === 'function') r.claim(surface, OWNER, payload);
      else if (r && typeof r.claimOwnership === 'function') r.claimOwnership(surface, OWNER, payload);
    } catch(e) {}
  }
  function event(name, meta){
    try {
      var r = registry();
      var payload = Object.assign({ version: VERSION, owner: OWNER }, meta || {});
      if (r && typeof r.event === 'function') r.event(name, payload);
      else if (r && typeof r.trackEvent === 'function') r.trackEvent(name, payload);
    } catch(e) {}
  }
  function clone(obj){ return JSON.parse(JSON.stringify(obj)); }
  function arrayify(v){ return Array.isArray(v) ? v : (v == null || v === '' ? [] : [v]); }
  function slug(v){ return String(v || '').trim().toLowerCase().replace(/[^a-z0-9]+/g,'-').replace(/^-+|-+$/g,''); }
  function unique(values){ var seen={}; return arrayify(values).map(slug).filter(function(v){ if(!v || seen[v]) return false; seen[v]=true; return true; }); }

  var doctrine = [
    'NotificationRegistry owns notification object contracts, lifecycle, priority, scope, retention, and delivery eligibility.',
    'NotificationRenderer owns display only and must not own persistence or source generation.',
    'Source systems such as comments, reactions, posts, stories, moderation, universe, galaxy, event, and chat may emit notification intents later, but NotificationRegistry defines the canonical shape.',
    'No notification source may write directly to NotificationRenderer.',
    'All future Firebase listeners must be tracked through DSDRegistry.trackFirebase and owned by a future NotificationRealtimeController.',
    'Notification persistence, realtime delivery, dispatch, digest, and UI delivery are disabled in this build.',
    'Moderation and safety notifications may override digest settings but still require audit/registry ownership.',
    'Future universe/galaxy/chat notifications are scopes, not separate notification systems.'
  ];

  var sourceTypes = {
    postPublished: { key:'postPublished', owner:'CanonicalPostStore', defaultPriority:'social', scope:'post' },
    postMention: { key:'postMention', owner:'CanonicalPostStore', defaultPriority:'social', scope:'post' },
    commentCreated: { key:'commentCreated', owner:'CommentRenderer', defaultPriority:'social', scope:'comment' },
    replyCreated: { key:'replyCreated', owner:'CommentRenderer', defaultPriority:'social', scope:'comment' },
    reactionCreated: { key:'reactionCreated', owner:'ReactionRenderer', defaultPriority:'engagement', scope:'reaction' },
    profileFollow: { key:'profileFollow', owner:'ProfileRenderer', defaultPriority:'social', scope:'profile' },
    profileUpdate: { key:'profileUpdate', owner:'ProfileRenderer', defaultPriority:'system', scope:'profile' },
    storyPublished: { key:'storyPublished', owner:'StoriesRenderer', defaultPriority:'social', scope:'story', future:true },
    storyReply: { key:'storyReply', owner:'StoriesRenderer', defaultPriority:'social', scope:'story', future:true },
    moderationReportReceived: { key:'moderationReportReceived', owner:'ModerationRegistry', defaultPriority:'moderation', scope:'moderation' },
    moderationAction: { key:'moderationAction', owner:'ModerationRegistry', defaultPriority:'critical', scope:'moderation' },
    appealUpdate: { key:'appealUpdate', owner:'ModerationRegistry', defaultPriority:'critical', scope:'moderation', future:true },
    universeInvite: { key:'universeInvite', owner:'GovernanceRegistry', defaultPriority:'community', scope:'universe', future:true },
    universeRoleAssigned: { key:'universeRoleAssigned', owner:'GovernanceRegistry', defaultPriority:'community', scope:'universe', future:true },
    galaxyInvite: { key:'galaxyInvite', owner:'GovernanceRegistry', defaultPriority:'community', scope:'galaxy', future:true },
    galaxyRoleAssigned: { key:'galaxyRoleAssigned', owner:'GovernanceRegistry', defaultPriority:'community', scope:'galaxy', future:true },
    galaxyPost: { key:'galaxyPost', owner:'CanonicalPostStore', defaultPriority:'community', scope:'galaxy', future:true },
    galaxyStory: { key:'galaxyStory', owner:'StoriesRenderer', defaultPriority:'community', scope:'galaxyStory', future:true },
    galaxyChatMention: { key:'galaxyChatMention', owner:'MessagingRenderer', defaultPriority:'community', scope:'galaxyChat', future:true },
    eventInvite: { key:'eventInvite', owner:'GovernanceRegistry', defaultPriority:'community', scope:'event', future:true },
    eventUpdate: { key:'eventUpdate', owner:'GovernanceRegistry', defaultPriority:'community', scope:'event', future:true },
    systemAnnouncement: { key:'systemAnnouncement', owner:'PlatformOperationsRegistry', defaultPriority:'system', scope:'system', future:true }
  };

  var priorities = {
    critical: { key:'critical', rank:100, digestEligible:false, bypassMute:true, examples:['moderationAction','appealUpdate','safety'] },
    moderation: { key:'moderation', rank:85, digestEligible:false, bypassMute:true, examples:['reports','review','escalation'] },
    community: { key:'community', rank:65, digestEligible:true, bypassMute:false, examples:['galaxy','universe','event','chat'] },
    social: { key:'social', rank:50, digestEligible:true, bypassMute:false, examples:['comments','mentions','follows','stories'] },
    engagement: { key:'engagement', rank:35, digestEligible:true, bypassMute:false, examples:['likes','reactions','shares'] },
    system: { key:'system', rank:25, digestEligible:true, bypassMute:false, examples:['profile','platform','tips'] },
    marketing: { key:'marketing', rank:10, digestEligible:true, bypassMute:false, examples:['promotions','announcements'] }
  };

  var scopes = {
    global: { key:'global', owner:'PlatformOperationsRegistry' },
    system: { key:'system', owner:'PlatformOperationsRegistry' },
    profile: { key:'profile', owner:'ProfileRenderer' },
    post: { key:'post', owner:'CanonicalPostStore' },
    comment: { key:'comment', owner:'CommentRenderer' },
    reaction: { key:'reaction', owner:'ReactionRenderer' },
    story: { key:'story', owner:'StoriesRenderer', future:true },
    moderation: { key:'moderation', owner:'ModerationRegistry' },
    universe: { key:'universe', owner:'GovernanceRegistry', future:true },
    galaxy: { key:'galaxy', owner:'GovernanceRegistry', future:true },
    galaxyStory: { key:'galaxyStory', owner:'StoriesRenderer', future:true },
    galaxyChat: { key:'galaxyChat', owner:'MessagingRenderer', future:true },
    event: { key:'event', owner:'GovernanceRegistry', future:true }
  };

  var lifecycleStates = {
    intentCreated: { key:'intentCreated', terminal:false },
    queued: { key:'queued', terminal:false },
    persisted: { key:'persisted', terminal:false },
    delivered: { key:'delivered', terminal:false },
    read: { key:'read', terminal:false },
    dismissed: { key:'dismissed', terminal:true },
    expired: { key:'expired', terminal:true },
    suppressed: { key:'suppressed', terminal:true },
    failed: { key:'failed', terminal:true }
  };

  var deliveryChannels = {
    inAppBell: { key:'inAppBell', owner:'NotificationRenderer', enabled:false },
    inAppPanel: { key:'inAppPanel', owner:'NotificationRenderer', enabled:false },
    toast: { key:'toast', owner:'NotificationRenderer', enabled:false },
    email: { key:'email', owner:'ExternalEmailService', enabled:false, future:true },
    push: { key:'push', owner:'FuturePushService', enabled:false, future:true },
    digest: { key:'digest', owner:'NotificationRegistry', enabled:false, future:true }
  };

  var retentionProfiles = {
    ephemeral: { key:'ephemeral', days:7, appliesTo:['toast','lowValue'] },
    standard: { key:'standard', days:90, appliesTo:['social','engagement','community'] },
    moderation: { key:'moderation', days:365, appliesTo:['moderation','critical'] },
    system: { key:'system', days:180, appliesTo:['system'] },
    auditLinked: { key:'auditLinked', days:null, appliesTo:['actions','appeals','legalSafety'], auditRequired:true }
  };

  var aggregationRules = {
    reactionBurst: { key:'reactionBurst', sourceTypes:['reactionCreated'], windowMinutes:30, aggregateBy:['recipientUid','postId','reactionType'] },
    commentThread: { key:'commentThread', sourceTypes:['commentCreated','replyCreated'], windowMinutes:15, aggregateBy:['recipientUid','postId'] },
    galaxyChatMentions: { key:'galaxyChatMentions', sourceTypes:['galaxyChatMention'], windowMinutes:10, aggregateBy:['recipientUid','galaxyId'] },
    moderationBatch: { key:'moderationBatch', sourceTypes:['moderationReportReceived'], windowMinutes:5, aggregateBy:['queueKey','severity'] }
  };

  var pathContracts = {
    notificationItem: 'notifications/{recipientUserId}/items/{notificationId}',
    notificationInbox: 'notifications/{recipientUserId}/inbox/{notificationId}',
    notificationUnreadIndex: 'notifications/{recipientUserId}/unread/{notificationId}',
    sourceIndex: 'notificationSources/{sourceType}/{sourceId}/items/{notificationId}',
    scopeIndex: 'notificationScopes/{scopeType}/{scopeId}/items/{notificationId}',
    digestQueue: 'notificationDigests/{recipientUserId}/queues/{digestId}',
    preferences: 'notificationPreferences/{userId}',
    auditLink: 'auditLogs/{scopeType}/{scopeId}/items/{auditId}'
  };

  function normalizePriority(p){ p = slug(p || 'social'); return priorities[p] ? p : 'social'; }
  function normalizeSource(s){ s = String(s || 'systemAnnouncement'); return sourceTypes[s] ? s : 'systemAnnouncement'; }
  function buildIntent(input){
    input = input || {};
    var sourceType = normalizeSource(input.sourceType);
    var source = sourceTypes[sourceType] || sourceTypes.systemAnnouncement;
    var priority = normalizePriority(input.priority || source.defaultPriority);
    var scope = slug(input.scope || source.scope || 'system');
    return {
      version: VERSION,
      notificationId: input.notificationId || null,
      sourceType: sourceType,
      sourceId: input.sourceId || null,
      actorUid: input.actorUid || null,
      recipientUid: input.recipientUid || null,
      targetType: slug(input.targetType || scope),
      targetId: input.targetId || null,
      scope: scope,
      priority: priority,
      state: 'intentCreated',
      channels: unique(input.channels || ['inAppBell','inAppPanel']),
      digestEligible: !!(priorities[priority] && priorities[priority].digestEligible),
      retentionProfile: priority === 'critical' || priority === 'moderation' ? 'moderation' : 'standard',
      firebaseReadsEnabled: FIREBASE_READS_ENABLED,
      firebaseWritesEnabled: FIREBASE_WRITES_ENABLED,
      realtimeEnabled: REALTIME_ENABLED,
      dispatchEnabled: DISPATCH_ENABLED,
      pathPreview: pathContracts.notificationItem
    };
  }

  function getManifest(){
    return clone({
      version: VERSION,
      owner: OWNER,
      gates: {
        firebaseReadsEnabled: FIREBASE_READS_ENABLED,
        firebaseWritesEnabled: FIREBASE_WRITES_ENABLED,
        realtimeEnabled: REALTIME_ENABLED,
        dispatchEnabled: DISPATCH_ENABLED,
        uiDeliveryEnabled: UI_DELIVERY_ENABLED,
        digestEnabled: DIGEST_ENABLED
      },
      doctrine: doctrine,
      sourceTypes: sourceTypes,
      priorities: priorities,
      scopes: scopes,
      lifecycleStates: lifecycleStates,
      deliveryChannels: deliveryChannels,
      retentionProfiles: retentionProfiles,
      aggregationRules: aggregationRules,
      pathContracts: pathContracts,
      dependencies: {
        notificationCenter: !!window.DSDNotifications321,
        notificationDependencyGraph: !!window.DSDNotificationDependencyGraph,
        firebaseSocialSchema: !!window.DSDFirebaseSocialSchema,
        realtimeOwnershipAudit: !!window.DSDRealtimeOwnershipAudit,
        moderationRegistry: !!window.DSDModerationRegistry,
        governanceRegistry: !!window.DSDGovernanceRegistry,
        visibilityLayer: !!window.DSDVisibilityLayer,
        surfaceRegistry: !!window.DSDSurfaceRegistry
      }
    });
  }

  window.DSDNotificationRegistry = {
    version: VERSION,
    owner: OWNER,
    gates: {
      firebaseReadsEnabled: FIREBASE_READS_ENABLED,
      firebaseWritesEnabled: FIREBASE_WRITES_ENABLED,
      realtimeEnabled: REALTIME_ENABLED,
      dispatchEnabled: DISPATCH_ENABLED,
      uiDeliveryEnabled: UI_DELIVERY_ENABLED,
      digestEnabled: DIGEST_ENABLED
    },
    getManifest: getManifest,
    buildIntent: buildIntent,
    getSourceTypes: function(){ return clone(sourceTypes); },
    getPriorities: function(){ return clone(priorities); },
    getScopes: function(){ return clone(scopes); },
    getLifecycleStates: function(){ return clone(lifecycleStates); },
    getPathContracts: function(){ return clone(pathContracts); },
    normalizePriority: normalizePriority,
    normalizeSource: normalizeSource
  };

  claim('notification-registry:doctrine', { doctrine: doctrine, dispatchEnabled: DISPATCH_ENABLED });
  claim('notification-registry:sources', { sources: Object.keys(sourceTypes), futureSources: Object.keys(sourceTypes).filter(function(k){ return !!sourceTypes[k].future; }) });
  claim('notification-registry:priorities', { priorities: Object.keys(priorities), criticalBypass: true, digestEligible: ['community','social','engagement','system','marketing'] });
  claim('notification-registry:scopes', { scopes: Object.keys(scopes), futureScopes: ['story','universe','galaxy','galaxyStory','galaxyChat','event'] });
  claim('notification-registry:lifecycle', { states: Object.keys(lifecycleStates), terminalStates: ['dismissed','expired','suppressed','failed'] });
  claim('notification-registry:delivery-channels', { channels: Object.keys(deliveryChannels), allEnabled: false });
  claim('notification-registry:retention', { profiles: Object.keys(retentionProfiles), auditLinked: true });
  claim('notification-registry:aggregation', { rules: Object.keys(aggregationRules), digestEnabled: DIGEST_ENABLED });
  claim('notification-registry:path-contracts', { paths: pathContracts, firebaseReadsEnabled: FIREBASE_READS_ENABLED, firebaseWritesEnabled: FIREBASE_WRITES_ENABLED });
  claim('notification-registry:dependencies', getManifest().dependencies);
  event('NOTIFICATION_REGISTRY_BOOT', { status:'registered', dispatchEnabled: DISPATCH_ENABLED, firebaseWritesEnabled: FIREBASE_WRITES_ENABLED, realtimeEnabled: REALTIME_ENABLED });
})();
